import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import cx from 'classnames';
import axios from 'axios';
import _ from 'lodash';
import {
  API_NEWCLIENT,
  API_KEY,
  API_CLIENTS_NAME,
  API_GET_PLANS,
  API_GROUPS
} from '../../../utils/constants';
import { useSelector, useDispatch } from 'react-redux';
import { generateContent } from '../../../store/slices/generalState';
import { useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import BusinessHours from '../AboutSection/BusinessHours/BusinessHours';
import { setSelectedClient } from '../../../store/slices/selectedClient';
import { selectedClientSelector } from '../../../store/slices/selectedClient';
import Links from '../Links';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../../components/ui/dialog';

import {
  Home,
  Star,
  FileText,
  Image,
  Bell,
  LogOut,
  Menu,
  Facebook,
  Instagram,
  Check,
  X,
  CalendarDays,
  Building2,
  Search,
  Save,
  Phone,
  MapPin,
  Briefcase,
  Clock,
  MapPinned,
  Globe,
  LucideMail,
  Share2,
  ArrowLeft,
  Tag,
  Settings,
  Sparkles,
  Calendar
} from 'lucide-react';

import { useSidebar } from '../../../contexts/SidebarContext';
import { Spinner } from '../../../components/ui/spinner';

export const daysOrder = {
  Sunday: 1,
  Monday: 2,
  Tuesday: 3,
  Wednesday: 4,
  Thursday: 5,
  Friday: 6,
  Saturday: 7
};

const CANADA_HOLIDAYS = [
  'Victoria Day',
  'Canada Day',
  'Canadian Thanksgiving',
  'Remembrance Day'
];

const getHolidaysFromEnv = () => {
  const envHolidays = process.env.REACT_APP_HOLIDAYS_LIST;
  if (!envHolidays) return [];
  try {
    // Si la variable es un JSON, parsea, si es string separado por coma, divide
    if (envHolidays.trim().startsWith('[')) {
      return JSON.parse(envHolidays);
    }
    return envHolidays
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
};

const collectFormikErrorMessages = (errorObject, path = '', bucket = []) => {
  if (!errorObject) return bucket;

  if (typeof errorObject === 'string') {
    bucket.push(path ? `${path}: ${errorObject}` : errorObject);
    return bucket;
  }

  if (Array.isArray(errorObject)) {
    errorObject.forEach((item, index) => {
      collectFormikErrorMessages(item, `${path}[${index}]`, bucket);
    });
    return bucket;
  }

  if (typeof errorObject === 'object') {
    Object.entries(errorObject).forEach(([key, value]) => {
      const nextPath = path ? `${path}.${key}` : key;
      collectFormikErrorMessages(value, nextPath, bucket);
    });
  }

  return bucket;
};

const ContentStrategySettings = ({ callbackFromParent, defaultData = {} }) => {
  const { user } = useUser();
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const history = useHistory();
  const dispatch = useDispatch();

  const selectedClient = useSelector(
    (state) => state.selectedClient?.selectedClient?.id
  );
  const selectedClientData = useSelector(
    selectedClientSelector
  )?.selectedClient;
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [gettingPlans, setGettingPlans] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [gettingIndustries, setGettingIndustries] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "We can't edit the client due to an error."
  );

  const holidaysList = getHolidaysFromEnv();

  const initialValues = {
    clientName: '',
    timeZone: '',
    serviceArea: [],
    secondaryServiceArea: [],
    description: '',
    businessHours: {
      monday: '9/18',
      tuesday: '9/18',
      wednesday: '9/18',
      thursday: '9/18',
      friday: '9/18',
      saturday: '9/18',
      sunday: '9/18'
    },
    mergeFields: {
      '~Address': '',
      '~Phone': '',
      '~Website': '',
      '~Email': ''
    },
    industry: '',
    primaryService: [],
    additionalService: [],
    attributes: [],
    promotions: [],
    holidays: [],
    igTags: [],
    fbTags: [],
    negativeKeywords: [],
    galleries: {
      stockPhotos: { enabled: true },
      appLibrary: { enabled: false }
    },
    logoUrl: '',
    brand: {
      primaryColor: '#3B5998',
      secondaryColor: '#8392A5'
    },
    tier: '',
    automaticReviews: true,
    singlePerson: false,
    clientFlags: []
  };

  const userInfo = useSelector((state) => state.auth.userInfo);
  const timeZones = [
    { value: 1, label: 'Eastern Daylight Time - (GMT-4)' },
    { value: 2, label: 'Central Daylight Time - (GMT-5)' },
    { value: 3, label: 'Mountain Daylight Time - (GMT-6)' },
    { value: 4, label: 'Mountain Standard Time - (GMT-7)' },
    { value: 5, label: 'Pacific Daylight Time - (GMT-7)' },
    { value: 6, label: 'Alaska Daylight Time - (GMT-8)' },
    { value: 7, label: 'Hawaii-Aleutian Standard Time - (GMT-10)' }
  ];

  const attributeList = [
    { value: 1, label: 'Curbside Pickup' },
    { value: 2, label: 'Deliver' },
    { value: 3, label: 'Dine-in' },
    { value: 4, label: 'Drive through' },
    { value: 5, label: 'No-contact delivery' },
    { value: 6, label: 'Outdoor seating' },
    { value: 7, label: 'Takeout' },
    { value: 8, label: 'Assistive hearing loop' },
    { value: 9, label: 'Beach wheelchairs' },
    { value: 10, label: 'Mobility scooter rental' },
    { value: 11, label: 'Passenger loading area' },
    { value: 12, label: 'Wheelchair-accessible elevator' },
    { value: 13, label: 'Wheelchair-accessible entrance' },
    { value: 14, label: 'Wheelchair-accessible parking' },
    { value: 15, label: 'Wheelchair-accessible restroom' },
    { value: 16, label: 'Wheelchair-accessible seating' },
    { value: 17, label: 'Adults only' },
    { value: 18, label: 'Family-friendly' },
    { value: 19, label: 'Kid-friendly' },
    { value: 20, label: 'LGBTQ+ friendly' },
    { value: 21, label: 'Transgender safespace' },
    { value: 22, label: 'Accepting new patients' },
    { value: 23, label: 'Accepts reservations' },
    { value: 24, label: 'Appointment required' },
    { value: 25, label: 'Membership required' },
    { value: 26, label: 'Online events calendar' },
    { value: 27, label: 'Reservations recommended' },
    { value: 28, label: 'Reservations required' },
    { value: 29, label: 'Beach access' },
    { value: 30, label: 'Bicycles for rent' },
    { value: 31, label: 'Boats for rent' },
    { value: 32, label: 'Game room' },
    { value: 33, label: 'Horseback riding' },
    { value: 34, label: 'Jogging track' },
    { value: 35, label: 'Nightclub' },
    { value: 36, label: 'Scuba diving' },
    { value: 37, label: 'Snorkeling' },
    { value: 38, label: 'Tennis' },
    { value: 39, label: 'Water skiing' },
    { value: 40, label: 'Identifies as black owned' },
    { value: 41, label: 'Identifies as veteran-led' },
    { value: 42, label: 'Identifies as women-led' },
    { value: 43, label: 'Cash Only' },
    { value: 44, label: 'Checks' },
    { value: 45, label: 'Debit Cards' },
    { value: 46, label: 'NFC Mobile Payments' },
    { value: 47, label: 'American Express' },
    { value: 48, label: 'Master Card' },
    { value: 49, label: 'Visa' },
    { value: 50, label: 'Discover' },
    { value: 51, label: 'Diners club' }
  ];

  const PRIMARY_COLOR = '#3B5998';
  const ERROR_COLOR = '#dc3545';
  const BORDER_COLOR = '#e2e8f0';
  const SELECT_HEIGHT = '56px';

  const getBaseSelectStyles = (hasError) => ({
    control: (provided, state) => ({
      ...provided,
      minHeight: SELECT_HEIGHT,
      height: SELECT_HEIGHT,
      borderRadius: '0.75rem',
      borderWidth: '2px',
      borderColor: hasError
        ? ERROR_COLOR
        : state.isFocused
        ? PRIMARY_COLOR
        : BORDER_COLOR,
      boxShadow: 'none',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        borderColor: hasError ? ERROR_COLOR : PRIMARY_COLOR
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 1rem',
      minHeight: SELECT_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem'
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#0f172a'
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#94a3b8'
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      margin: 0,
      padding: 0
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? PRIMARY_COLOR : '#94a3b8',
      paddingRight: '1rem'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#94a3b8',
      paddingRight: '1rem',
      '&:hover': {
        color: '#475569'
      }
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 4,
      borderRadius: '0.75rem',
      border: `1px solid ${BORDER_COLOR}`,
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '1rem',
      padding: '0.75rem 1rem',
      backgroundColor: state.isSelected
        ? PRIMARY_COLOR
        : state.isFocused
        ? 'rgba(59, 89, 152, 0.1)'
        : 'transparent',
      color: state.isSelected ? '#ffffff' : '#0f172a',
      cursor: 'pointer'
    })
  });

  const customMultiSelectorStyle = {
    control: (provided, state) => ({
      ...provided,
      minHeight: SELECT_HEIGHT,
      borderRadius: '0.75rem',
      borderWidth: '2px',
      borderColor: state?.isFocused ? PRIMARY_COLOR : BORDER_COLOR,
      boxShadow: 'none',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      padding: '0 4px',
      '&:hover': {
        borderColor: PRIMARY_COLOR
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0.5rem 1rem',
      minHeight: SELECT_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap'
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '1.125rem',
      color: '#94a3b8'
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '1.125rem',
      margin: 0,
      padding: 0
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state?.isFocused ? PRIMARY_COLOR : '#94a3b8',
      paddingRight: '1rem'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#94a3b8',
      paddingRight: '1rem',
      '&:hover': {
        color: '#475569'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 4,
      borderRadius: '0.75rem',
      border: `1px solid ${BORDER_COLOR}`,
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: 200
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '1rem',
      padding: '0.75rem 1rem',
      backgroundColor: state?.isSelected
        ? PRIMARY_COLOR
        : state?.isFocused
        ? 'rgba(59, 89, 152, 0.1)'
        : 'transparent',
      color: state?.isSelected ? '#ffffff' : '#0f172a',
      cursor: 'pointer'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#f4f4f4',
      border: `1px solid #f4f4f4`,
      borderRadius: '9999px',
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '1rem',
      margin: '2px 4px',
      padding: '0 0.25rem',
      display: 'flex',
      alignItems: 'center',
      boxShadow: 'none',
      minHeight: '32px',
      height: '32px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#0f172a',
      fontWeight: 700,
      fontSize: '0.75rem',
      padding: 0,
      marginRight: 4
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#64748b',
      borderRadius: '9999px',
      marginLeft: 2,
      fontWeight: 700,
      fontSize: '0.75rem',
      padding: '0 2px',
      transition: 'background 0.15s',
      '&:hover': {
        backgroundColor: '#e2e8f0',
        color: '#0f172a'
      }
    })
  };

  const validationSchema = yup.object().shape({
    // Validate only fields present in this Content Settings view.
    attributes: yup.array().of(yup.string()),
    promotions: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A promotion has an invalid format'
          )
      ),
    igTags: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A tag has an invalid format'
          )
      ),
    fbTags: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'A tag has an invalid format'
          )
      ),
    negativeKeywords: yup.array().of(yup.string()),
    additionalService: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'An additional service has an invalid format'
          )
      ),
    holidays: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'",@~#%$&*!_\-=:./?|\\()[\]{}<>]+$/,
            'Invalid format'
          )
      ),
    clientFlags: yup.array().of(yup.string())
  });

  const editing = Object.keys(defaultData).length > 0;

  const SingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props} className="selected-option">
      {children}
      <div className="selected-logo">
        <Connected />
      </div>
    </components.SingleValue>
  );

  const Option = (props) => (
    <components.Option {...props}>
      <div className="country-option">
        {props.data.label}
        <Disconnected />
      </div>
    </components.Option>
  );

  const Connected = () => (
    <img src="../assets/img/Connected.png" alt="connected" />
  );

  const Disconnected = () => (
    <img src="../assets/img/Disconnected.png" alt="disconnected" />
  );

  // Reset industries when selected client changes
  useEffect(() => {
    setIndustries([]);
  }, [selectedClient]);

  // Fetch plans and content strategies
  useEffect(() => {
    const getPlans = async () => {
      setGettingPlans(true);
      try {
        const response = await axios.get(`${API_GET_PLANS}`);
        const plans = response.data;
        const order = ['Starter Plan', 'Plus Plan', 'Pro Plan', 'Elite Plan'];
        const formattedPlans = plans.map((plan) => ({
          ...plan,
          value: plan.PK,
          label: plan.SK
        }));
        const sortedPlans = formattedPlans.sort(
          (a, b) => order.indexOf(a.label) - order.indexOf(b.label)
        );
        setPlans(sortedPlans);
      } catch (e) {
        console.error(e);
      } finally {
        setGettingPlans(false);
      }
    };

    const getIndustries = async () => {
      setGettingIndustries(true);
      try {
        const response = await axios.get(API_GROUPS, {
          headers: { Authorization: `bearer ${userInfo.accessToken}` }
        });
        const groupsData = response.data.groups;
        const newIndustries = groupsData?.map((option) => ({
          value: option.id,
          label: option.groupName
        }));
        setIndustries((prev) =>
          _.isEqual(prev, newIndustries) ? prev : newIndustries
        );
      } catch (e) {
        console.error('API_GROUPS Error:', e);
      } finally {
        setGettingIndustries(false);
      }
    };

    if (plans.length === 0 && !gettingPlans) {
      getPlans();
    }
    if (industries.length === 0 && !gettingIndustries) {
      getIndustries();
    }
  }, []);

  const handlePut = async (data = {}) => {
    setLoading(true);
    const fallbackClientName =
      data?.clientName ||
      defaultData?.clientName ||
      selectedClientData?.clientName ||
      '';

    if (!fallbackClientName) {
      const missingClientMessage =
        'Save blocked: clientName is empty. The form can validate, but save requires a clientName for API_NEWCLIENT.';
      console.error('[ContentStrategySettings] ' + missingClientMessage, {
        formData: data,
        defaultData,
        selectedClientData
      });
      setErrorMessage(
        'Cannot save because clientName is empty. Select a client and try again.'
      );
      setErrorModalOpen(true);
      setLoading(false);
      return;
    }

    let newClientName = fallbackClientName.replace(/\s+/g, ' ');
    newClientName = newClientName.replace(/^\s+|\s+$/g, '');
    newClientName = newClientName.replace(/'/g, "'");

    const clientData = {
      ...data,
      id: defaultData?.id,
      clientName: newClientName
    };

    try {
      const response = await axios({
        method: 'post',
        url: API_NEWCLIENT,
        data: clientData
      });

      if (response.status === 200) {
        setSuccessModalOpen(true);

        callbackFromParent(clientData);

        if (clientData?.id) {
          const clData = await axios.get(
            `${API_CLIENTS_NAME}${clientData.clientName}`,
            {
              headers: {
                'X-API-KEY': API_KEY,
                Authorization: `bearer ${userInfo.accessToken}`
              }
            }
          );
          // No redirect - stay on Content Strategy page
          // let newUrl = clData?.data?.clientName
          //   .replace(/\s+/g, '%20')
          //   .replace(/\//g, '%2F')
          //   .replace(/'/g, '%1D')
          //   .replace(/'/g, '%1E');
          // history.push(`/client/${newUrl}`);
          if (clData.data) {
            // Merge clientFlags from saved data in case API_CLIENTS_NAME doesn't return them
            dispatch(
              setSelectedClient({
                ...clData.data,
                clientFlags:
                  clData.data.clientFlags ?? clientData.clientFlags ?? []
              })
            );
          }
        } else {
          const clData = await axios.get(
            `${API_CLIENTS_NAME}${clientData.clientName}`,
            {
              headers: {
                'X-API-KEY': API_KEY,
                Authorization: `bearer ${userInfo.accessToken}`
              }
            }
          );
          // No redirect for new clients either - stay on page
          // let newUrl = clientData.clientName
          //   .replace(/\s+/g, '%20')
          //   .replace(/\//g, '%2F')
          //   .replace(/'/g, '%1D')
          //   .replace(/'/g, '%1E');
          // history.push('/calendar');
          if (clData.data) {
            dispatch(
              setSelectedClient({
                ...clData.data,
                clientFlags:
                  clData.data.clientFlags ?? clientData.clientFlags ?? []
              })
            );
            dispatch(generateContent());
          }
        }
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Unknown error';
      console.error('[ContentStrategySettings] Save failed', {
        apiMessage,
        status: error?.response?.status,
        responseData: error?.response?.data,
        payload: clientData
      });
      setErrorMessage(`Unable to save client settings. Details: ${apiMessage}`);
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      onSubmit={handlePut}
      enableReinitialize
      validateOnBlur={true}
      initialValues={
        editing
          ? {
              ...defaultData,
              ...(!defaultData.businessHours?.monday
                ? { businessHours: initialValues.businessHours }
                : {}),
              ...(!defaultData?.brand?.primaryColor
                ? { brand: initialValues.brand }
                : {}),
              // Fallback to Redux clientFlags when the API returns nothing useful
              // (undefined, null, or empty array) but Redux has the real values
              ...(!defaultData?.clientFlags?.length &&
              selectedClientData?.clientFlags?.length > 0
                ? { clientFlags: selectedClientData.clientFlags }
                : {})
            }
          : initialValues
      }
      validationSchema={validationSchema}>
      {(props) => {
        const {
          setFieldValue,
          submitForm,
          validateForm,
          values,
          handleChange,
          handleBlur,
          errors,
          touched
        } = props;

        return (
          <>
            {/* Header */}

            {/* Content Area */}
            <Form autoComplete="off" className="needs-validation" noValidate>
              <main className="p-6 lg:p-10 space-y-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-12 w-12 rounded-xl"
                      onClick={() => history.goBack()}>
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                      <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                        Content Settings,{' '}
                        {selectedClientData
                          ? `${selectedClientData.clientName}`
                          : ''}
                      </h1>
                      <p className="text-base text-muted-foreground mt-1">
                        Configure your content preferences and posting settings
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      size="lg"
                      disabled={loading}
                      onClick={async () => {
                        const validationErrors = await validateForm();
                        const allMessages =
                          collectFormikErrorMessages(validationErrors);

                        if (allMessages.length > 0) {
                          const detailsToShow = allMessages
                            .slice(0, 5)
                            .join(' | ');
                          console.error(
                            '[ContentStrategySettings] Save blocked by Formik validation',
                            {
                              validationErrors,
                              values
                            }
                          );
                          setErrorMessage(
                            `Cannot save because there are validation errors. ${detailsToShow}`
                          );
                          setErrorModalOpen(true);
                          return;
                        }

                        console.log(
                          '[ContentStrategySettings] Validation passed. Submitting form.'
                        );
                        submitForm();
                      }}
                      style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3 flex items-center justify-center">
                      <Save className="h-5 w-5" />
                      {loading && <Spinner className="h-5 w-5" />}
                      {!loading && 'Save Changes'}
                    </Button>
                  </div>
                </div>

                <Card className="shadow-md rounded-2xl border-0">
                  <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Share2 className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl lg:text-2xl font-bold">
                          Social Networks
                        </CardTitle>
                        <CardDescription className="text-base mt-1">
                          Configure which social platforms to post on
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6 px-6 lg:px-8 pb-8">
                    <div className="flex items-center justify-between py-4 border-b border-border">
                      <div className="space-y-1">
                        <Label className="text-base font-semibold">
                          Disable Facebook Posting
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Disable Facebook posting for this client
                        </p>
                      </div>
                      <Switch
                        id="clientFlags.NO_FB"
                        className="data-[state=checked]:bg-primary scale-75"
                        checked={
                          values?.clientFlags?.includes('NO_FB') || false
                        }
                        onCheckedChange={(checked) => {
                          const currentFlags = values?.clientFlags || [];
                          if (checked) {
                            setFieldValue('clientFlags', [
                              ...currentFlags,
                              'NO_FB'
                            ]);
                          } else {
                            setFieldValue(
                              'clientFlags',
                              currentFlags.filter((flag) => flag !== 'NO_FB')
                            );
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between py-4 border-b border-border">
                      <div className="space-y-1">
                        <Label className="text-base font-semibold">
                          Disable Instagram Posting
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Disable Instagram posting for this client
                        </p>
                      </div>
                      <Switch
                        id="clientFlags.NO_IG"
                        className="data-[state=checked]:bg-primary scale-75"
                        checked={
                          values?.clientFlags?.includes('NO_IG') || false
                        }
                        onCheckedChange={(checked) => {
                          const currentFlags = values?.clientFlags || [];
                          if (checked) {
                            setFieldValue('clientFlags', [
                              ...currentFlags,
                              'NO_IG'
                            ]);
                          } else {
                            setFieldValue(
                              'clientFlags',
                              currentFlags.filter((flag) => flag !== 'NO_IG')
                            );
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between py-4 border-b border-border">
                      <div className="space-y-1">
                        <Label className="text-base font-semibold">
                          Disable Google Business Posting
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Disable Google My Business posting
                        </p>
                      </div>
                      <Switch
                        id="clientFlags.NO_GMB"
                        className="data-[state=checked]:bg-primary scale-75"
                        checked={
                          values?.clientFlags?.includes('NO_GMB') || false
                        }
                        onCheckedChange={(checked) => {
                          const currentFlags = values?.clientFlags || [];
                          if (checked) {
                            setFieldValue('clientFlags', [
                              ...currentFlags,
                              'NO_GMB'
                            ]);
                          } else {
                            setFieldValue(
                              'clientFlags',
                              currentFlags.filter((flag) => flag !== 'NO_GMB')
                            );
                          }
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between py-4">
                      <div className="space-y-1">
                        <Label className="text-base font-semibold">
                          Disable AI-Generated Images
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Disable AI-generated images for this account
                        </p>
                      </div>
                      <Switch
                        id="clientFlags.NO_AI"
                        className="data-[state=checked]:bg-primary scale-75"
                        checked={
                          values?.clientFlags?.includes('NO_AI') || false
                        }
                        onCheckedChange={(checked) => {
                          const currentFlags = values?.clientFlags || [];
                          if (checked) {
                            setFieldValue('clientFlags', [
                              ...currentFlags,
                              'NO_AI'
                            ]);
                          } else {
                            setFieldValue(
                              'clientFlags',
                              currentFlags.filter((flag) => flag !== 'NO_AI')
                            );
                          }
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-md rounded-2xl border-0">
                  <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Tag className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl lg:text-2xl font-bold">
                          Content Tags & Preferences
                        </CardTitle>
                        <CardDescription className="text-base mt-1">
                          Configure your content preferences and tags
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-8 px-6 lg:px-8 pb-8">
                    {/* Attributes and Instagram Tags Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Settings className="h-5 w-5 text-muted-foreground" />
                          Attributes
                        </Label>
                        <p className="text-sm text-muted-foreground -mt-1">
                          Select attributes that describe your content style
                        </p>
                        <Select
                          id="attributes"
                          isMulti
                          options={attributeList}
                          value={attributeList.filter((attr) =>
                            values?.attributes?.includes(attr.label)
                          )}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              'attributes',
                              selectedOption
                                ? selectedOption.map((attr) => attr.label)
                                : []
                            );
                          }}
                          placeholder="Select attributes"
                          styles={customMultiSelectorStyle}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Tag className="h-5 w-5 text-muted-foreground" />
                          Instagram Tags
                        </Label>
                        <p className="text-sm text-muted-foreground -mt-1">
                          Tags to include in your Instagram posts
                        </p>
                        <CreatableSelect
                          id="igTags"
                          name="igTags"
                          isMulti
                          isClearable={false}
                          value={values?.igTags?.map((tag, index) => ({
                            value: index,
                            label: tag
                          }))}
                          noOptionsMessage={() => 'Type an Instagram tag'}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              'igTags',
                              selectedOption
                                ? selectedOption.map((tag) => tag.label)
                                : []
                            );
                          }}
                          options={[]}
                          placeholder="Type an Instagram tag"
                          styles={customMultiSelectorStyle}
                        />
                      </div>
                    </div>

                    {/* Promotions and Facebook Tags Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-muted-foreground" />
                          Promotions
                        </Label>
                        <p className="text-sm text-muted-foreground -mt-1">
                          Current promotions to highlight in your content
                        </p>
                        <CreatableSelect
                          id="promotions"
                          name="promotions"
                          isMulti
                          isClearable={false}
                          value={values?.promotions?.map((promo, index) => ({
                            value: index,
                            label: promo
                          }))}
                          noOptionsMessage={() => 'Type a promotion'}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              'promotions',
                              selectedOption
                                ? selectedOption.map((promo) => promo.label)
                                : []
                            );
                          }}
                          options={[]}
                          placeholder="Type a promotion"
                          styles={customMultiSelectorStyle}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Tag className="h-5 w-5 text-muted-foreground" />
                          Facebook Tags
                        </Label>
                        <p className="text-sm text-muted-foreground -mt-1">
                          Tags to include in your Facebook posts
                        </p>
                        <CreatableSelect
                          id="fbTags"
                          name="fbTags"
                          isMulti
                          isClearable={false}
                          value={values?.fbTags?.map((tag, index) => ({
                            value: index,
                            label: tag
                          }))}
                          noOptionsMessage={() => 'Type a Facebook tag'}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              'fbTags',
                              selectedOption
                                ? selectedOption.map((tag) => tag.label)
                                : []
                            );
                          }}
                          options={[]}
                          placeholder="Type a Facebook tag"
                          styles={customMultiSelectorStyle}
                        />
                      </div>
                    </div>

                    {/* Negative Keywords and Additional Services Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Tag className="h-5 w-5 text-muted-foreground" />
                          Negative Keywords
                        </Label>
                        <p className="text-sm text-muted-foreground -mt-1">
                          Keywords to avoid in your generated content
                        </p>
                        <CreatableSelect
                          id="negativeKeywords"
                          name="negativeKeywords"
                          isMulti
                          isClearable={false}
                          value={values?.negativeKeywords?.map(
                            (keyword, index) => ({
                              value: index,
                              label: keyword
                            })
                          )}
                          noOptionsMessage={() => 'Type a negative keyword'}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              'negativeKeywords',
                              selectedOption
                                ? selectedOption.map((keyword) => keyword.label)
                                : []
                            );
                          }}
                          options={[]}
                          placeholder="Type a negative keyword"
                          styles={customMultiSelectorStyle}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Settings className="h-5 w-5 text-muted-foreground" />
                          Additional Services
                        </Label>
                        <p className="text-sm text-muted-foreground -mt-1">
                          Extra services to promote in your content
                        </p>
                        <CreatableSelect
                          id="additionalService"
                          name="additionalService"
                          isMulti
                          isClearable={false}
                          value={values?.additionalService?.map(
                            (servA, index) => ({
                              value: index,
                              label: servA
                            })
                          )}
                          noOptionsMessage={() => 'Type an additional service'}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              'additionalService',
                              selectedOption
                                ? selectedOption.map((servA) => servA.label)
                                : []
                            );
                          }}
                          options={[]}
                          placeholder="Type an additional service"
                          styles={customMultiSelectorStyle}
                        />
                      </div>
                    </div>

                    {/* Holidays Section */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                          Holidays
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Select holidays to create special content for
                        </p>
                      </div>
                      {[
                        {
                          title: 'United States Holidays',
                          holidays: holidaysList
                        },
                        { title: 'Canada Holidays', holidays: CANADA_HOLIDAYS }
                      ]
                        .filter((group) => group.holidays.length > 0)
                        .map((group) => (
                          <div key={group.title} className="space-y-3 pt-2">
                            <p className="text-sm font-semibold">
                              {group.title}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {group.holidays.map((holiday) => (
                                <div
                                  key={holiday}
                                  className="flex items-center space-x-3">
                                  <Checkbox
                                    id={`holidays.${holiday}`}
                                    checked={
                                      values?.holidays?.includes(holiday) ||
                                      false
                                    }
                                    onCheckedChange={(checked) => {
                                      const currentHolidays =
                                        values?.holidays || [];
                                      if (checked) {
                                        setFieldValue('holidays', [
                                          ...currentHolidays,
                                          holiday
                                        ]);
                                      } else {
                                        setFieldValue(
                                          'holidays',
                                          currentHolidays.filter(
                                            (flag) => flag !== holiday
                                          )
                                        );
                                      }
                                    }}
                                    className="h-5 w-5 rounded border-2 border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                  />
                                  <label
                                    htmlFor={`holidays.${holiday}`}
                                    className="text-base cursor-pointer">
                                    {holiday}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </main>
            </Form>

            {/* Success Modal */}
            <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
              <DialogContent className="sm:max-w-md text-center py-10">
                <DialogHeader className="items-center">
                  <DialogTitle className="text-2xl font-bold text-foreground">
                    Success!
                  </DialogTitle>
                  <DialogDescription className="text-base text-muted-foreground mt-2">
                    The client was updated successfully.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-center mt-4">
                  <Button
                    type="button"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                    onClick={() => setSuccessModalOpen(false)}>
                    OK
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Error Modal */}
            <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
              <DialogContent className="sm:max-w-md text-center py-10">
                <DialogHeader className="items-center">
                  <DialogTitle className="text-2xl font-bold text-foreground">
                    Error!
                  </DialogTitle>
                  <DialogDescription className="text-base text-muted-foreground mt-2">
                    {errorMessage}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-center mt-4">
                  <Button
                    type="button"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                    onClick={() => setErrorModalOpen(false)}>
                    OK
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Footer */}
          </>
        );
      }}
    </Formik>
  );
};

export default ContentStrategySettings;
